import type BaseEnumType from "@/app/services/baseEnumType";
import { translate } from "@/plugins/i18n";
import type { Location } from "vue-router";
import Key from "@/app/types/common/key";
import type { Instant } from "@js-joda/core";
import type { Maybe } from "@/components/utils/typeHelpers";
import type { BmIconName } from "@/components/controls/bmIconMap";

export interface ActivityAssociationLine {
  name?: string; // Appears in Bold:
  value: string;
}
export interface ActivityAssociation {
  textLines: ActivityAssociationLine[];
  route?: Location;
  name?: string;
  icon?: BmIconName;
}

export interface ActivityLogEntryCardType {
  timestamp: Instant;
  taskType: string;
  entityType: string;
  status: EnumLogEntryStatus | null;
  association: ActivityAssociation | null;
}

export class EnumLogEntryStatus implements BaseEnumType {
  private static allValues: EnumLogEntryStatus[] = []; // make sure this is top-most

  public static created = new EnumLogEntryStatus(
    0,
    "Created",
    "crm_log_created",
    "primary",
    "brand02"
  );
  public static cancelled = new EnumLogEntryStatus(
    1,
    "Cancelled",
    "crm_log_cancelled",
    "error",
    "errorMedium"
  );
  public static completed = new EnumLogEntryStatus(
    2,
    "Completed",
    "crm_log_completed",
    "success"
  );

  public key: number;
  public value: string;
  public translationKey: string;
  public chipColour: string;
  public chipColourDark?: string;

  private constructor(
    key: number,
    value: string,
    translationKey: string,
    chipColour: string,
    chipColourDark?: string
  ) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    this.chipColour = chipColour;
    this.chipColourDark = chipColourDark;
    EnumLogEntryStatus.allValues.push(this);
  }

  public get translation() {
    return translate(Key.fromString(this.translationKey));
  }

  public equals(other: Maybe<EnumLogEntryStatus>): boolean {
    return this.key === other?.key;
  }

  public isCreated() {
    return this.equals(EnumLogEntryStatus.created);
  }
  public isCancelled() {
    return this.equals(EnumLogEntryStatus.cancelled);
  }
  public isCompleted() {
    return this.equals(EnumLogEntryStatus.completed);
  }

  public static getValues(): EnumLogEntryStatus[] {
    return EnumLogEntryStatus.allValues;
  }

  public static getByKey(key: number): EnumLogEntryStatus {
    const found = EnumLogEntryStatus.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumLogEntryStatus - Key not found: ${key}`);
    }

    return found;
  }
}
